import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from '@/lib/motion';
import { 
  Play, X, Clock, Eye, Heart, Share, Download,
  YoutubeLogo, LinkedinLogo, InstagramLogo,
  Factory, Ruler, Fire, Sparkle, Wrench, Certificate,
  ArrowRight, MagnifyingGlass, Funnel, CircleNotch
} from '@phosphor-icons/react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageMeta } from '../components/PageMeta';
import { PageHero } from '../components/PageHero';
import { Breadcrumb } from '../components/Breadcrumb';
import { QuickActions } from '../components/QuickActions';
import { ScrollToTop } from '../components/ScrollToTop';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Video Modal Component
const VideoModal = ({ content, onClose }) => {
  if (!content) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-4xl bg-obsidian"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white/80 hover:text-white"
        >
          <X weight="bold" className="w-8 h-8" />
        </button>
        
        {/* Video Placeholder */}
        <div className="aspect-video bg-black flex items-center justify-center">
          <div className="text-center">
            <Play weight="fill" className="w-20 h-20 text-bronze mx-auto mb-4" />
            <p className="text-white/60 text-sm">Video would load here</p>
            <p className="text-white/40 text-xs mt-2">{content.videoUrl}</p>
          </div>
        </div>
        
        {/* Content Info */}
        <div className="p-6">
          <h3 className="font-display text-xl font-bold text-white mb-2">{content.title}</h3>
          <p className="text-white/60 text-sm mb-4">{content.description}</p>
          
          <div className="flex items-center gap-6 text-sm text-white/40">
            <span className="flex items-center gap-2">
              <Eye weight="bold" className="w-4 h-4" />
              {content.views} views
            </span>
            <span className="flex items-center gap-2">
              <Heart weight="bold" className="w-4 h-4" />
              {content.likes} likes
            </span>
            <span className="flex items-center gap-2">
              <Clock weight="bold" className="w-4 h-4" />
              {content.duration}
            </span>
          </div>
          
          {/* Tags */}
          {content.tags && content.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {content.tags.map(tag => (
                <span key={tag} className="px-2 py-1 bg-white/10 text-white/60 text-xs">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Content Card Component
const ContentCard = ({ content, onClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="group cursor-pointer bg-white border border-black/5 hover:shadow-xl transition-all duration-300"
      onClick={() => onClick(content)}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <img loading="lazy" decoding="async" 
          src={content.thumbnail?.startsWith('http') ? content.thumbnail : `${API_BASE}${content.thumbnail}`}
          alt={content.title}
          width={640}
          height={360}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
          <div className="w-14 h-14 bg-bronze/90 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play weight="fill" className="w-6 h-6 text-obsidian ml-1" />
          </div>
        </div>
        {/* Duration Badge */}
        <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/80 text-white text-xs font-medium">
          {content.duration}
        </div>
        {/* Featured Badge */}
        {content.featured && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-bronze text-obsidian text-xs font-bold uppercase">
            Featured
          </div>
        )}
      </div>

      {/* Content Info */}
      <div className="p-5">
        <h3 className="font-display font-bold text-obsidian mb-2 line-clamp-2 group-hover:text-bronze transition-colors">
          {content.title}
        </h3>
        <p className="text-sm text-concrete line-clamp-2 mb-4">
          {content.description}
        </p>
        
        {/* Stats */}
        <div className="flex items-center gap-4 text-xs text-concrete">
          <span className="flex items-center gap-1">
            <Eye weight="bold" className="w-3.5 h-3.5" />
            {content.views}
          </span>
          <span className="flex items-center gap-1">
            <Heart weight="bold" className="w-3.5 h-3.5" />
            {content.likes}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default function ThoughtLeadershipPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [contentItems, setContentItems] = useState([]);
  const [categories, setCategories] = useState([{ id: 'all', label: 'All Content' }]);
  const [loading, setLoading] = useState(true);

  // Fetch content from CMS
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, categoriesRes] = await Promise.all([
          fetch(`${API_BASE}/api/cms/thought-leadership`),
          fetch(`${API_BASE}/api/cms/thought-leadership/categories`)
        ]);
        
        if (contentRes.ok) {
          const data = await contentRes.json();
          setContentItems(data);
        }
        
        if (categoriesRes.ok) {
          const cats = await categoriesRes.json();
          setCategories(cats);
        }
      } catch (err) {
        console.error('Failed to fetch content:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Filter content based on category and search
  const filteredContent = contentItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
    return matchesCategory && matchesSearch;
  });

  // Get featured content for hero
  const featuredContent = contentItems.filter(item => item.featured).slice(0, 3);

  return (
    <div className="min-h-screen bg-gallery">
      <PageMeta 
        title="Thought Leadership | Technical Videos & Insights | IHG Industry"
        description="Watch fabrication process videos, technical deep-dives, and expert tips from IHG Industry's metalwork specialists."
        keywords="metal fabrication videos, architectural metalwork tutorials, CNC laser cutting process"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Resources', path: '/resources' },
          { name: 'Thought Leadership', path: '/thought-leadership' }
        ]}
      />
      <Navbar />
      <QuickActions variant="sidebar" />
      <QuickActions variant="fab" />

      {/* Hero - Matching consistent design pattern */}
      <PageHero
        label="EXPERT INSIGHTS"
        title={<>THOUGHT <span className="text-bronze">LEADERSHIP</span></>}
        description="Explore our fabrication processes, technical deep-dives, and expert tips. See how world-class architectural metalwork comes to life."
        backgroundImage="https://files.ihgbrands.com/ihg-ind/generated/hero_main.webp"
      />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-4">
          <Breadcrumb 
            items={[
              { label: 'Home', href: '/' },
              { label: 'Resources', href: '/resources' }
            ]} 
            currentPage="Thought Leadership" 
          />
        </div>
      </div>

      {/* Social Links Bar */}
      <section className="py-6 bg-obsidian">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <span className="text-white/60 text-sm">Follow our channels:</span>
            <a 
              href="https://www.youtube.com/@ihgindustry" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-semibold text-sm hover:bg-red-700 transition-colors"
            >
              <YoutubeLogo weight="fill" className="w-5 h-5" />
              YouTube
            </a>
            <a 
              href="https://www.linkedin.com/company/ihgind" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              <LinkedinLogo weight="fill" className="w-5 h-5" />
              LinkedIn
            </a>
            <a 
              href="https://www.instagram.com/ihgindustry" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              <InstagramLogo weight="fill" className="w-5 h-5" />
              Instagram
            </a>
          </div>
        </div>
      </section>

      {/* Featured Videos */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <span className="w-12 h-px bg-bronze" />
              <span className="font-body text-[10px] font-medium uppercase tracking-[0.3em] text-bronze">Editor's Picks</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-obsidian">
              Featured <span className="text-bronze">Content</span>
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredContent.map(content => (
              <ContentCard 
                key={content.id} 
                content={content} 
                onClick={setSelectedContent}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Filter & Search */}
      <section className="py-6 bg-gallery border-y border-black/5 sticky top-16 z-30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-obsidian text-white'
                      : 'bg-white border border-black/10 text-concrete hover:border-obsidian hover:text-obsidian'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-64">
              <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-concrete" />
              <input
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-black/10 focus:border-obsidian focus:outline-none text-sm bg-white"
              />
            </div>
          </div>
        </div>
      </section>

      {/* All Content Grid */}
      <section className="py-20 md:py-24 bg-gallery">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <span className="w-12 h-px bg-bronze" />
              <span className="font-body text-[10px] font-medium uppercase tracking-[0.3em] text-bronze">{filteredContent.length} Videos</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-obsidian">
              Browse <span className="text-bronze">Library</span>
            </h2>
          </div>

          {filteredContent.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-concrete">No videos found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredContent.map(content => (
                <ContentCard 
                  key={content.id} 
                  content={content} 
                  onClick={setSelectedContent}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-obsidian">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center justify-center gap-4 mb-6">
              <span className="w-12 h-px bg-bronze" />
              <span className="font-body text-[10px] font-medium uppercase tracking-[0.3em] text-bronze">Stay Updated</span>
              <span className="w-12 h-px bg-bronze" />
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mb-6">
              NEVER MISS A <span className="text-bronze">VIDEO</span>
            </h2>
            <p className="font-body text-base text-white/50 max-w-xl mx-auto mb-10">
              Subscribe to our YouTube channel for weekly updates on fabrication techniques, project showcases, and expert insights.
            </p>
            <a
              href="https://www.youtube.com/@ihgindustry?sub_confirmation=1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-4 px-8 py-4 bg-red-600 text-white font-body text-xs font-semibold uppercase tracking-[0.2em] transition-all duration-300 hover:bg-red-700 group"
            >
              <YoutubeLogo weight="fill" className="w-5 h-5" />
              Subscribe on YouTube
              <ArrowRight weight="bold" className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedContent && (
          <VideoModal content={selectedContent} onClose={() => setSelectedContent(null)} />
        )}
      </AnimatePresence>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
